import { Component, OnDestroy, OnInit } from '@angular/core';
import { EMPTY, of, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { DropDownItem } from '../../../general/components/dropdown/dropdown.item';
import { PaymentStateService } from '../../../payment/services/payment-state.service';
import { BookingSlot } from '../../models/booking-slot';
import { PickupPoint } from '../../models/pickup-point';
import { BookingOrderService } from '../../services/booking-order.service';
import { BookingSlotSelectPolicyEnum } from '../../services/booking-slot-select-policy.enum';
import { BookingSlotStateService } from '../../services/booking-slot-state.service';
import { BookingStepService } from '../../services/booking-step.service';
import { ShopSettingsService } from '../../../general/services/shop-settings.service';
import { DisplayDateFormatEnum } from '../../../general/models/display-date-format-enum';
import { OrderLineType } from '../../../order/models/order-line-type';
import { OrderStateService } from '../../../order/services/order-state.service';

@Component({
  selector: 'app-time-slot-simple-list',
  templateUrl: './time-slot-simple-list.component.html',
  styleUrls: ['./time-slot-simple-list.component.css']
})
export class TimeSlotSimpleListComponent implements OnInit, OnDestroy {

  displayDateFormat = DisplayDateFormatEnum.shotWeekdayAndDate;
  pickUpPointerItems: DropDownItem[];
  pickUpPointGroupItems: DropDownItem[];
  private _initializeBookingSlotStateService: Subscription;
  private _shiftDaySubscription: Subscription;
  private _selectedPickupPointId: number = null;

  constructor(
    public bookingSlotStateService: BookingSlotStateService,
    private paymentStateService: PaymentStateService,
    private bookingOrderService: BookingOrderService,
    private bookingStepService: BookingStepService,
    private shopSettingsService: ShopSettingsService,
    private orderStateService: OrderStateService,
  ) { }

  ngOnInit(): void {
    if (this.bookingOrderService.areaAreaItemPack
      && this.bookingOrderService.areaAreaItemPack.pickupPoints
      && this.bookingOrderService.areaAreaItemPack.pickupPoints.length !== 0) {

      let pickupPointGroups = this.bookingOrderService.areaAreaItemPack.pickupPointGroups;
      if (this.bookingOrderService.stockLocationsIds) {
        pickupPointGroups = pickupPointGroups.filter(item => this.bookingOrderService.stockLocationsIds.includes(item.id));
      }

      const pickUpPointGroupItems = pickupPointGroups
        .map(pickupPointGroup => new DropDownItem(pickupPointGroup.id, pickupPointGroup.name));
      this.pickUpPointGroupItems = pickUpPointGroupItems?.length === 1 ? [] : [new DropDownItem(null, 'Please select')];
      this.pickUpPointGroupItems.push.apply(this.pickUpPointGroupItems, pickUpPointGroupItems);
      this._updatePickupPointsList();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeInitializeBookingSlotStateService();
    this._unsubscribeShiftDaySubscription();
  }

  nextDayClick(): void {
    this.shiftDay(1);
  }

  prevDayClick(): void {
    this.shiftDay(-1);
  }

  get canShiftDate(): boolean {
    return this.bookingSlotStateService.canShiftDate;
  }

  shiftDay(offset: number): void {
    if (!this.canShiftDate) {
      return;
    }

    this._unsubscribeShiftDaySubscription();
    this._shiftDaySubscription = this.bookingStepService.canResetOrderQuestionPopup()
      .pipe(
        switchMap(
          canShift => {
            if (!canShift) {
              return EMPTY;
            }
            return this.bookingSlotStateService.shiftDay(offset);
          }
        ),
        switchMap(
          result => {
            if (!result || this.bookingSlotStateService.selectedSlot == null) {
              return this.bookingSlotStateService.shiftNearestSlot(offset);
            }
            return of(true);
          }
        )
      )
      .subscribe(
        _ => {
          if (!this.shopSettingsService.shopSettings || !this.shopSettingsService.shopSettings.multiOrderMode) {
            this.resetOrder(offset > 0 ? BookingSlotSelectPolicyEnum.FirstSlot : BookingSlotSelectPolicyEnum.LastSlot);
          } else {
            this.bookingOrderService.singleCache.ordersCache.resetIsCacheLinesQtyCalculatedForAllChachedOders();
            this.bookingOrderService.setSelectedBookingDateAndSlots(this.selectedDate, this.selectedBookingSlots, true);
            this._updatePickupPointsList();
          }
        });
  }

  get selectedDate(): Date {
    if (!this.bookingSlotStateService.selectedSlot) {
      return this.bookingSlotStateService.selectedDate;
    }
    return this.bookingSlotStateService.getSelectedBookingDate(this.bookingSlotStateService.selectedSlot);
  }

  get selectedBookingSlots(): BookingSlot[] {
    if (!this.bookingSlotStateService.selectedSlot) {
      return null;
    }
    return [this.bookingSlotStateService.selectedSlot];
  }

  resetOrder(bookingSlotSelectPolicy: BookingSlotSelectPolicyEnum = BookingSlotSelectPolicyEnum.None) {
    this.bookingOrderService.setSelectedBookingDateAndSlots(this.selectedDate, this.selectedBookingSlots, true);
    this._unsubscribeInitializeBookingSlotStateService();
    this._initializeBookingSlotStateService = this.bookingSlotStateService.initialize(bookingSlotSelectPolicy)
      .subscribe(
        _ => {
          this.paymentStateService.reset();
          this.bookingOrderService.setSelectedBookingDateAndSlots(this.selectedDate, this.selectedBookingSlots, true);
        }
      );
  }

  private _unsubscribeInitializeBookingSlotStateService() {
    if (this._initializeBookingSlotStateService) {
      this._initializeBookingSlotStateService.unsubscribe();
      this._initializeBookingSlotStateService = null;
    }
  }

  private _unsubscribeShiftDaySubscription(): void {
    if (this._shiftDaySubscription) {
      this._shiftDaySubscription.unsubscribe();
      this._shiftDaySubscription = null;
    }
  }

  set selectedPickupPointGroupId(pickupPointGroupId: number) {
    const resetCurrentOrder = this.bookingOrderService.selectedPickupPointGroupId != null;
    this.bookingOrderService.selectedPickupPointGroupId = pickupPointGroupId;
    if (resetCurrentOrder) {
      this.bookingOrderService.clearSelectedDateOrder();
    }
    this._updatePickupPointsList();
  }

  private _updatePickupPointsList(): void {
    this.pickUpPointerItems = [new DropDownItem(null, 'Please select')];
    const pickUpPointItems = this.bookingOrderService.areaAreaItemPack.pickupPoints
      .filter(x => this.selectedPickupPointGroupId ? x.groupId === this.selectedPickupPointGroupId : !x.groupId)
      .sort((a, b) => a.sequence - b.sequence)
      .map(pickupPoint => new DropDownItem(pickupPoint.id, pickupPoint.name));
    this.pickUpPointerItems.push.apply(this.pickUpPointerItems, pickUpPointItems);
    if (pickUpPointItems && !pickUpPointItems.some((dropdown: DropDownItem) => dropdown.value === this.selectedPickupPointId)) {
      this.selectedPickupPointId = null;
    }
  }

  get selectedPickupPointGroupId(): number {
    if (this.pickUpPointGroupItems?.length === 1) {
      return this.pickUpPointGroupItems[0].value;
    }
    return this.bookingOrderService.selectedPickupPointGroupId;
  }

  get selectedPickupPointId(): number {
    return this.bookingOrderService.selectedPickupPointId;
  }

  set selectedPickupPointId(pickupPointId: number) {
    this.bookingOrderService.selectedPickupPointId = pickupPointId;
    this.bookingOrderService.singleCache.updateInvalidReason();
    this.orderStateService.orderSaveToLocalStorage();
  }

  get pickupPointsExist(): boolean {
    return !!this.bookingOrderService.areaAreaItemPack.pickupPoints;
  }

  get pickupPointGroupsExist(): boolean {
    return !!this.bookingOrderService.areaAreaItemPack.pickupPointGroups;
  }

  get dateSlotClasses(): string {
    return this.pickupPointsExist ?
      this.pickupPointGroupsExist ? 'mt-3 mt-lg-0 col-sm-9 col-xga-4' : 'mt-4 mt-lg-0 col-sm-9 col-xga-5'
      : 'mt-4 mt-lg-0 col-12 col-sm-9 col-lg-6';
  }

  get timeSlotClasses(): string {
    return this.pickupPointsExist ?
      this.pickupPointGroupsExist ? 'mt-3 mt-lg-0 col-sm-3 col-xga-2' : 'mt-4 mt-lg-0 col-sm-3 col-xga-3'
      : 'mt-4 mt-lg-0 col-12 col-sm-3 col-lg-6';
  }

  get pickuPointClasses(): string {
    return this.pickupPointGroupsExist ? 'mt-4 mt-lg-0 col-sm-6 col-md-6 col-lg-6 col-xga-3' : 'mt-4 mt-lg-0 col-sm-12 col-md-12 col-lg-12 col-xga-4';
  }

  onUpdatedBookingSlotEvent() {
    if (!this.shopSettingsService.shopSettings || !this.shopSettingsService.shopSettings.multiOrderMode) {
      this.resetOrder();
    } else {
      this.bookingOrderService.singleCache.ordersCache.resetIsCacheLinesQtyCalculatedForAllChachedOders();
      this.bookingOrderService.setSelectedBookingDateAndSlots(this.selectedDate, this.selectedBookingSlots, true);
    }
  }
}
